import { AlignCenter, AlignLeft, AlignRight, AlignVerticalJustifyCenter, AlignVerticalJustifyEnd, AlignVerticalJustifyStart, ChevronDown } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import type { CurvaDocument } from "@/document/model/document";
import { findDocumentStyle } from "@/document/model/documentStyles";
import type { RichTextModel, TextInspectorRange, TypeStyle } from "@/document/model/text";
import { textTypeStyleInspector, textWithAppliedTypography } from "@/document/model/text";
import { loadGoogleFontWeights, weightsForGoogleFont } from "@/lib/GoogleFontLoader";
import { pickNearestFontWeight, STANDARD_FONT_WEIGHTS } from "@/lib/fontWeightLabels";
import { AppliedStyleRow } from "./AppliedStyleRow";
import { FontPickerPopover } from "./FontPickerPopover";
import { FontWeightLabel } from "./FontWeightLabel";
import { LineHeightField } from "./LineHeightField";
import { FontSizeField } from "./FontSizeField";
import { PanelSelect, PanelSelectItem } from "./PanelSelect";
import { NumberField } from "./NumberField";
import { PanelSection, PanelSectionStyleButton } from "./PanelSection";

interface TypographySectionProps {
  text: RichTextModel;
  styleRange: TextInspectorRange;
  onChange: (text: RichTextModel) => void;
  onStylePatch: (patch: Partial<TypeStyle>) => void;
  onStylePreview?: (patch: Partial<TypeStyle>) => void;
  onStylePreviewClear?: () => void;
  document?: CurvaDocument;
  appliedStyleId?: string;
  onOpenStyles?: () => void;
  onDetachStyle?: () => void;
  onEditStyle?: () => void;
}

export function TypographySection({
  text,
  styleRange,
  onChange,
  onStylePatch,
  onStylePreview,
  onStylePreviewClear,
  document,
  appliedStyleId,
  onOpenStyles,
  onDetachStyle,
  onEditStyle,
}: TypographySectionProps) {
  const [fontPickerOpen, setFontPickerOpen] = useState(false);
  const displayText = useMemo(() => {
    if (!appliedStyleId || !document) {
      return text;
    }
    const style = findDocumentStyle(document, appliedStyleId);
    if (style?.kind !== "text") {
      return text;
    }
    return textWithAppliedTypography(text, style.typography);
  }, [appliedStyleId, document, text]);
  const inspector = useMemo(() => textTypeStyleInspector(displayText, styleRange), [styleRange, displayText]);
  const appliedFamily = inspector.fontFamily === "mixed" ? displayText.style.fontFamily : inspector.fontFamily;
  const currentWeight = inspector.fontWeight === "mixed" ? displayText.style.fontWeight : inspector.fontWeight;
  const [availableWeights, setAvailableWeights] = useState<number[]>(() =>
    inspector.fontFamily === "mixed" ? [...STANDARD_FONT_WEIGHTS] : weightsForGoogleFont(appliedFamily),
  );

  useEffect(() => {
    if (inspector.fontFamily === "mixed") {
      setAvailableWeights([...STANDARD_FONT_WEIGHTS]);
      return;
    }

    let cancelled = false;
    setAvailableWeights(weightsForGoogleFont(appliedFamily));
    void loadGoogleFontWeights(appliedFamily).then(weights => {
      if (!cancelled) {
        setAvailableWeights(weights);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [appliedFamily, inspector.fontFamily]);

  const previewFont = useCallback((family: string) => {
    const weights = weightsForGoogleFont(family);
    onStylePreview?.({
      fontFamily: family,
      fontWeight: pickNearestFontWeight(currentWeight, weights),
    });
    void loadGoogleFontWeights(family);
  }, [currentWeight, onStylePreview]);

  const commitFont = useCallback((family: string) => {
    void loadGoogleFontWeights(family).then(weights => {
      onStylePreviewClear?.();
      onStylePatch({
        fontFamily: family,
        fontWeight: pickNearestFontWeight(currentWeight, weights),
      });
      setFontPickerOpen(false);
    });
  }, [currentWeight, onStylePatch, onStylePreviewClear]);

  const cancelFontPicker = useCallback(() => {
    onStylePreviewClear?.();
    setFontPickerOpen(false);
  }, [onStylePreviewClear]);

  return (
    <>
      <PanelSection
        title="Text"
        headerRight={onOpenStyles ? <PanelSectionStyleButton label="Open text styles" onClick={onOpenStyles} /> : undefined}
      >
        {appliedStyleId && document ? (
          <AppliedStyleRow
            document={document}
            styleId={appliedStyleId}
            kind="text"
            onDetach={() => onDetachStyle?.()}
            onEdit={onEditStyle}
          />
        ) : (
        <div className="grid gap-2">
          <div className="grid grid-cols-3 gap-1">
            <LayoutButton active={text.textLayout === "AUTO_WIDTH"} label="Auto W" onClick={() => patchText({ textLayout: "AUTO_WIDTH" })} />
            <LayoutButton active={text.textLayout === "AUTO_HEIGHT"} label="Auto H" onClick={() => patchText({ textLayout: "AUTO_HEIGHT" })} />
            <LayoutButton active={text.textLayout === "FIXED_SIZE"} label="Fixed" onClick={() => patchText({ textLayout: "FIXED_SIZE" })} />
          </div>

          <div className="grid grid-cols-1 gap-2">
          <button
            type="button"
            onClick={() => setFontPickerOpen(true)}
            aria-label={`Font family: ${inspector.fontFamily === "mixed" ? "Mixed" : inspector.fontFamily}`}
            title={inspector.fontFamily === "mixed" ? "Mixed" : inspector.fontFamily}
            className="flex h-8 w-full items-center justify-between rounded border border-white/10 bg-white/[0.04] px-2 text-left text-[12px] text-slate-200 hover:bg-white/[0.07]"
          >
            <span className="min-w-0 truncate">{inspector.fontFamily === "mixed" ? "Mixed" : inspector.fontFamily}</span>
            <ChevronDown className="size-3.5 shrink-0 text-slate-500" aria-hidden="true" />
          </button>

          <div className="grid grid-cols-2 gap-2">
            <PanelSelect
              value={inspector.fontWeight === "mixed" ? "mixed" : String(inspector.fontWeight)}
              onValueChange={value => patchStyle({ fontWeight: Number(value) })}
            >
              {availableWeights.map(weight => (
                <PanelSelectItem key={weight} value={String(weight)}>
                  <FontWeightLabel weight={weight} />
                </PanelSelectItem>
              ))}
            </PanelSelect>
            <FontSizeField
              value={inspector.fontSize}
              fallbackValue={displayText.style.fontSize}
              min={1}
              max={400}
              onCommit={fontSize => patchStyle({ fontSize })}
            />
            <LineHeightField
              lineHeight={inspector.lineHeight}
              fontSize={inspector.fontSize === "mixed" ? displayText.style.fontSize : inspector.fontSize}
              onCommit={lineHeight => patchStyle({ lineHeight })}
            />
            <NumberField
              label="Track"
              value={inspector.letterSpacing === "mixed" ? displayText.style.letterSpacing : inspector.letterSpacing}
              mixed={inspector.letterSpacing === "mixed"}
              min={-50}
              max={200}
              step={0.1}
              onCommit={letterSpacing => patchStyle({ letterSpacing })}
            />
          </div>

          <div className="grid grid-cols-6 gap-1">
            <AlignButton active={inspector.horizontalAlign === "LEFT"} mixed={inspector.horizontalAlign === "mixed"} label="Align left" onClick={() => patchStyle({ horizontalAlign: "LEFT" })} icon={<AlignLeft className="size-4" />} />
            <AlignButton active={inspector.horizontalAlign === "CENTER"} mixed={inspector.horizontalAlign === "mixed"} label="Align center" onClick={() => patchStyle({ horizontalAlign: "CENTER" })} icon={<AlignCenter className="size-4" />} />
            <AlignButton active={inspector.horizontalAlign === "RIGHT"} mixed={inspector.horizontalAlign === "mixed"} label="Align right" onClick={() => patchStyle({ horizontalAlign: "RIGHT" })} icon={<AlignRight className="size-4" />} />
            <AlignButton active={inspector.verticalAlign === "TOP"} mixed={inspector.verticalAlign === "mixed"} label="Align top" onClick={() => patchStyle({ verticalAlign: "TOP" })} icon={<AlignVerticalJustifyStart className="size-4" />} />
            <AlignButton active={inspector.verticalAlign === "CENTER"} mixed={inspector.verticalAlign === "mixed"} label="Align middle" onClick={() => patchStyle({ verticalAlign: "CENTER" })} icon={<AlignVerticalJustifyCenter className="size-4" />} />
            <AlignButton active={inspector.verticalAlign === "BOTTOM"} mixed={inspector.verticalAlign === "mixed"} label="Align bottom" onClick={() => patchStyle({ verticalAlign: "BOTTOM" })} icon={<AlignVerticalJustifyEnd className="size-4" />} />
          </div>
          </div>
        </div>
        )}
      </PanelSection>

      {fontPickerOpen ? (
        <FontPickerPopover
          appliedFamily={appliedFamily}
          onPreview={previewFont}
          onCommit={commitFont}
          onClose={cancelFontPicker}
        />
      ) : null}
    </>
  );

  function patchText(patch: Partial<Pick<RichTextModel, "textLayout">>): void {
    onChange({ ...text, ...patch });
  }

  function patchStyle(patch: Partial<TypeStyle>): void {
    onStylePatch(patch);
  }
}

function LayoutButton({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      className={`h-8 px-2 text-[11px] ${active ? "bg-sky-500/20 text-sky-100" : "bg-white/[0.04] text-slate-300 hover:bg-white/[0.07]"}`}
    >
      {label}
    </Button>
  );
}

function AlignButton({
  active,
  mixed,
  label,
  icon,
  onClick,
}: {
  active: boolean;
  mixed: boolean;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={active ? "bg-sky-500/20 text-sky-100" : mixed ? "bg-white/[0.02] text-slate-500 hover:bg-white/[0.07]" : "bg-white/[0.04] text-slate-300 hover:bg-white/[0.07]"}
    >
      {icon}
    </Button>
  );
}
